import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Badge } from '../components/ui/badge';
import { Clock, CheckCircle, Code, Rocket, FileSearch, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DashboardPage() {
  const { t } = useLanguage();
  const [stats, setStats] = useState(null);
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, proposalsRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/proposals`)
      ]);
      setStats(statsRes.data);
      setProposals(proposalsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { key: 'inReview', label: t('dashboard.inReview'), icon: FileSearch, color: 'text-blue-400', bgColor: 'bg-blue-500/20' },
    { key: 'approved', label: t('dashboard.approved'), icon: CheckCircle, color: 'text-purple-400', bgColor: 'bg-purple-500/20' },
    { key: 'inDevelopment', label: t('dashboard.inDevelopment'), icon: Code, color: 'text-yellow-400', bgColor: 'bg-yellow-500/20' },
    { key: 'live', label: t('dashboard.live'), icon: Rocket, color: 'text-green-400', bgColor: 'bg-green-500/20' },
  ];

  const getStatusBadge = (status) => {
    const styles = {
      review: 'status-review',
      approved: 'status-approved',
      development: 'status-development',
      live: 'status-live',
    };
    return styles[status] || 'bg-slate-700/50 text-slate-300';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-[#02040A]" data-testid="dashboard-page">
      <Navbar />
      
      <div className="pt-24 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4" data-testid="dashboard-headline">
              {t('dashboard.headline')}
            </h1>
            <p className="text-slate-300">
              Real-time transparency into ecosystem development progress
            </p>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : (
            <>
              {/* Stats Cards */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
                {statCards.map((stat) => {
                  const Icon = stat.icon;
                  const statKey = stat.key === 'inReview' ? 'in_review' : 
                                  stat.key === 'inDevelopment' ? 'in_development' : stat.key;
                  const value = stats ? stats[statKey] : 0;
                  
                  return (
                    <div 
                      key={stat.key} 
                      className="stat-card"
                      data-testid={`stat-${stat.key}`}
                    >
                      <div className={`w-12 h-12 rounded-lg ${stat.bgColor} flex items-center justify-center mb-4`}>
                        <Icon className={`w-6 h-6 ${stat.color}`} />
                      </div>
                      <div className="text-3xl font-bold text-white mb-1">{value}</div>
                      <div className="text-sm text-slate-400">{stat.label}</div>
                    </div>
                  );
                })}
              </div>

              {/* Total Projects */}
              <div className="glass-card rounded-xl p-6 mb-8">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-white">{t('dashboard.total')}</h3>
                    <p className="text-slate-400 text-sm">All submitted proposals in the ecosystem</p>
                  </div>
                  <div className="text-4xl font-bold text-primary">{stats?.total || 0}</div>
                </div>
              </div>

              {/* Projects Table */}
              <div className="glass-card rounded-xl overflow-hidden">
                <div className="p-6 border-b border-white/10">
                  <h3 className="text-lg font-semibold text-white">Project Pipeline</h3>
                </div>
                
                {proposals.length === 0 ? (
                  <div className="p-12 text-center">
                    <p className="text-slate-400">No proposals yet. Be the first to submit!</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Team</th>
                          <th>Project</th>
                          <th>Submitted</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {proposals.map((proposal) => (
                          <tr key={proposal.id} data-testid={`proposal-row-${proposal.id}`}>
                            <td>
                              <div className="font-medium">{proposal.team_info.team_name}</div>
                              <div className="text-sm text-slate-400">{proposal.team_info.country}</div>
                            </td>
                            <td>
                              <div className="font-medium">
                                {proposal.custom_proposal_name || proposal.selected_dapp}
                              </div>
                              {proposal.incentive_preference && (
                                <div className="text-sm text-slate-400 capitalize">
                                  {proposal.incentive_preference.replace(/([A-Z])/g, ' $1').trim()}
                                </div>
                              )}
                            </td>
                            <td>
                              <div className="flex items-center gap-2 text-sm text-slate-400">
                                <Clock className="w-4 h-4" />
                                {formatDate(proposal.created_at)}
                              </div>
                            </td>
                            <td>
                              <Badge className={getStatusBadge(proposal.status)}>
                                {t(`statuses.${proposal.status}`)}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}
